import { RETENTION } from "./retention.ts";
/**
 * Rules for "Send a Verse", shared by the API (which enforces them) and the web form (which explains them).
 *
 * This is deliberately NOT a messaging feature: the sender picks a message from a fixed list, so there is no
 * free text to moderate. The only text a sender types is their first name, which is restricted to name characters.
 */

export const SEND_LIMITS = {
  /** A sent passage may be up to this many verses… */
  maxVerses: 5,
  /** …and this many words, so it fits the sticky header. */
  maxWords: 150,
  nameMax: 30,
  /** Distinct reports that take a verse down automatically. */
  reportsToHide: 3,
  /** Sent verses disappear after this many days. */
  expiresAfterDays: RETENTION.sentVerseDays,
} as const;

/**
 * The messages a sender can attach. The server stores only the `key`; the text is looked up when the recipient
 * finishes, so wording can be improved later without touching stored data. Warm and invitational, never pushy.
 */
export const SEND_MESSAGES = [
  { key: "thinking-of-you", text: "I was thinking of you when I read this." },
  { key: "hope", text: "I thought you could use a little hope today." },
  { key: "hard-season", text: "This verse carried me through a hard season. I hope it encourages you too." },
  { key: "favorite", text: "This is one of my favorite verses. I wanted to share it with you." },
  { key: "on-my-heart", text: "You've been on my heart. Here's some encouragement." },
  { key: "thankful", text: "I'm thankful for you, and this verse reminds me why." },
  { key: "peace", text: "I'm praying this brings you peace." },
  { key: "strength", text: "For the days when you need strength you don't feel you have." },
  { key: "what-do-you-think", text: "I'd love to hear what you think of this verse." },
  { key: "lets-talk", text: "Let's talk about this one sometime." },
] as const;

export type SendMessageKey = (typeof SEND_MESSAGES)[number]["key"];

/** The text for a stored key, or null for "no message" or an unknown key. */
export function messageText(key: string | null | undefined): string | null {
  return SEND_MESSAGES.find((m) => m.key === key)?.text ?? null;
}

export const isMessageKey = (key: unknown): key is SendMessageKey => typeof key === "string" && SEND_MESSAGES.some((m) => m.key === key);

/**
 * A sender's name shown as "A verse for you from Sarah" (and in link previews, which strangers see). Only letters,
 * spaces and the punctuation real names use, so it can't carry links, numbers, emoji or symbols.
 */
const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M}' .-]*$/u;

export type SendNameResult = { ok: true; value: string | null } | { ok: false; error: string };

export function checkSendName(raw: string | null | undefined): SendNameResult {
  if (raw === null || raw === undefined) return { ok: true, value: null };
  const value = raw.replace(/\s+/g, " ").trim();
  if (value === "") return { ok: true, value: null };
  if (value.length > SEND_LIMITS.nameMax) return { ok: false, error: `The name can be at most ${SEND_LIMITS.nameMax} characters.` };
  // "evil.com" is letters and a period, so a period between two letters is not allowed ("Dr. Kim" is fine).
  if (!NAME_RE.test(value) || /[\p{L}\p{M}]\.[\p{L}\p{M}]/u.test(value)) return { ok: false, error: "Use just your name: letters, spaces, apostrophes and hyphens." };
  return { ok: true, value };
}

export const SENT_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford base32: no I, L, O, U
export const SENT_CODE_RE = /^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{8}$/;

/** A random code from `random` (crypto.getRandomValues in production). 32 symbols → 5 bits each, no modulo bias. */
export function makeSentCode(random: (n: number) => Uint8Array): string {
  const bytes = random(8);
  return Array.from(bytes, (b) => SENT_CODE_ALPHABET[b & 31]).join("");
}

